/**
 * LinguetteCartella.jsx — la riga delle linguette della cartella del paziente
 * nello stile «Console» (PazienteDetailConsole.jsx).
 *
 * Sul computer, e per chi non e' un medico al telefono, e' la riga di prima,
 * con lo stesso DOM: le prove cercano le linguette da `.sp-iconbar` e dal
 * `title` (il nome intero), e verificaRotte dichiara `.sp-iconbar` fra i
 * riquadri che possono scorrere di lato.
 *
 * Sul telefono del medico (vistaTelefonoDelMedico: stile nuovo, fino a 480
 * px, medico collegato - lo stesso criterio di «Scatta» e «Carica»;
 * proposta telefono-stile-nuovo, punto 3, Alex 30/10/2026) la riga NON
 * scorre di lato: le prime quattro col nome breve e «Altro», che apre le
 * altre col nome intero e il loro numero (divideLinguette). Se la linguetta
 * accesa sta fra le altre, al posto di «Altro» c'e' il suo nome breve, acceso:
 * cosi' si vede sempre dove si e', e lo stesso tocco riapre l'elenco.
 *
 * Una linguetta e' uno stato della pagina (?vista=), non una rotta: nessuna
 * lettura in piu' e nessuna traccia nel registro degli accessi in piu'. Chi
 * sceglie una linguetta passa da `onScegli`, cioe' da setSchermata della
 * cartella, con la sua domanda se c'e' qualcosa da salvare.
 *
 * GLI ATTRIBUTI PER LE PROVE (solo sul telefono): data-linguette-telefono
 * sulla riga, data-linguetta (l'id) su ogni linguetta e su ogni voce di
 * «Altro», data-altro sul pulsante, data-altro-menu sull'elenco.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { divideLinguette } from '../../navigazione/telefono';

const Conta = ({ n }) => (n > 0 ? <span className="crt-conta rm-numeri">{n}</span> : null);

const LinguetteCartella = ({ linguette, attiva, onScegli, telefono = false }) => {
    const [aperto, setAperto] = useState(false);
    const pulsante = useRef(null);

    /* Esc chiude l'elenco e riporta il fuoco su «Altro». */
    useEffect(() => {
        if (!aperto) return undefined;
        const esc = (e) => { if (e.key === 'Escape') { setAperto(false); pulsante.current?.focus(); } };
        document.addEventListener('keydown', esc);
        return () => document.removeEventListener('keydown', esc);
    }, [aperto]);

    /* Fuori dal telefono l'elenco non esiste: se lo schermo si allarga con
       l'elenco aperto, al ritorno sul telefono non si ritrova aperto. */
    useEffect(() => { if (!telefono) setAperto(false); }, [telefono]);

    if (!telefono) {
        return (
            <nav className="sp-iconbar" aria-label="Sezioni della cartella">
                {linguette.map((m) => {
                    const acceso = attiva === m.id;
                    return (
                        <button key={m.id} type="button"
                                className={`sp-iconbtn${acceso ? ' attivo' : ''}`}
                                aria-current={acceso ? 'page' : undefined}
                                onClick={() => onScegli(m.id)}
                                title={m.titolo}>
                            {m.label}
                            <Conta n={m.conta} />
                        </button>
                    );
                })}
            </nav>
        );
    }

    const { riga, altro } = divideLinguette(linguette);
    const accesaFraLeAltre = altro.find((m) => m.id === attiva) || null;
    const scegli = (id) => { setAperto(false); onScegli(id); };

    return (
        <nav className="sp-iconbar sp-iconbar--telefono" aria-label="Sezioni della cartella" data-linguette-telefono="">
            {riga.map((m) => {
                const acceso = attiva === m.id;
                return (
                    <button key={m.id} type="button"
                            className={`sp-iconbtn${acceso ? ' attivo' : ''}`}
                            aria-current={acceso ? 'page' : undefined}
                            data-linguetta={m.id}
                            onClick={() => scegli(m.id)}
                            title={m.titolo}>
                        <span className="sp-linguetta-nome">{m.breve || m.label}</span>
                        <Conta n={m.conta} />
                    </button>
                );
            })}
            {altro.length > 0 && (
                <button type="button" ref={pulsante}
                        className={`sp-iconbtn sp-altro${accesaFraLeAltre ? ' attivo' : ''}`}
                        aria-current={accesaFraLeAltre ? 'page' : undefined}
                        aria-haspopup="menu" aria-expanded={aperto}
                        data-altro=""
                        title={accesaFraLeAltre ? `${accesaFraLeAltre.titolo} - le altre sezioni` : 'Le altre sezioni della cartella'}
                        onClick={() => setAperto((x) => !x)}>
                    <span className="sp-linguetta-nome">
                        {accesaFraLeAltre ? (accesaFraLeAltre.breve || accesaFraLeAltre.label) : 'Altro'}
                    </span>
                    <ChevronDown size={14} strokeWidth={2} aria-hidden="true" />
                </button>
            )}
            {aperto && (
                <>
                    <div className="sp-altro-velo" aria-hidden="true" onClick={() => setAperto(false)} />
                    <div className="sp-altro-menu" role="menu" aria-label="Altre sezioni della cartella" data-altro-menu="">
                        {altro.map((m) => {
                            const acceso = attiva === m.id;
                            return (
                                <button key={m.id} type="button" role="menuitem"
                                        className={`sp-altro-voce${acceso ? ' attivo' : ''}`}
                                        aria-current={acceso ? 'page' : undefined}
                                        data-linguetta={m.id}
                                        onClick={() => scegli(m.id)}
                                        title={m.titolo}>
                                    <span>{m.label}</span>
                                    <Conta n={m.conta} />
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </nav>
    );
};

export default LinguetteCartella;
